import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CancellationsApi from '../api/CancellationsApi';
import { handleUnauthorizedResponse } from '../api/http';
import { AuthContext, AuthData } from '../context/auth';
import { ErrorContext, ErrorData } from '../context/ErrorProvider';
import { CancellationResponse } from '../types/cancellations';
import { tableStyles } from '../util/styles';
import { getComparator, HeadCell, Order, stableSort } from '../util/tableUtil';
import { capitalize } from '../util/util';
import { StyledEmptyMessage } from './common/index';
import LargeCenterSpinner from './LargeCenterSpinner';
import TableActionConfirmationTrigger from './policy/TableActionConfirmationTrigger';
import SortableTable from './SortableTable';
import { successToast } from './Toast';

interface Props {
	cancellations: CancellationResponse[];
	authData: AuthData;
	errorData: ErrorData;
}

interface Data {
	cancellationId: string;
	dateCreated: string;
	effectiveDate: string;
	status: string;
	link: string;
	showReinstate: boolean;
	policyLocator: string;
}

function createData(
	cancellationId: string,
	dateCreated: string,
	effectiveDate: string,
	status: string,
	link: string,
	showReinstate: boolean,
	policyLocator: string,
): Data {
	return {
		cancellationId,
		dateCreated,
		effectiveDate,
		status,
		link,
		showReinstate,
		policyLocator,
	};
}

const CancellationsTable = (props: Props) => {
	const { cancellations, authData, errorData } = props;
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('cancellationId');
	const [loading, setLoading] = React.useState(false);
	const navigate = useNavigate();

	const emptyTable = !cancellations.length ? (
		<StyledEmptyMessage>No Cancellations</StyledEmptyMessage>
	) : null;
	const classes = tableStyles();

	const headCells: HeadCell[] = [
		{ id: 'cancellationId', rightAlign: false, label: 'Cancellation ID' },
		{ id: 'dateCreated', rightAlign: false, label: 'Date Created' },
		{ id: 'effectiveDate', rightAlign: false, label: 'Effective Date' },
		{ id: 'status', rightAlign: false, label: 'Current Status' },
		{ id: 'actions', rightAlign: false, label: '' },
	];

	const rows: any = cancellations.map((cancellation, index: number) => {
		return createData(
			cancellation.locator,
			cancellation.createdTimestamp,
			cancellation.effectiveTimestamp,
			capitalize(cancellation.state),
			`${cancellation.locator}/details`,
			(!cancellation.reinstatement ||
				cancellation?.reinstatement?.state === 'expired') &&
				cancellation.state === 'issued' &&
				authData.hasPermission('reinstatement.create'),
			cancellation.policyLocator,
		);
	});

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any) => {
			const reinstate = async () => {
				setLoading(true);

				try {
					const reinstatement = await CancellationsApi.reinstateCancellation(
						authData.auth.authorizationToken,
						row.cancellationId,
					);
					successToast('Successfully reinstated cancellation');

					if (reinstatement?.data?.locator) {
						navigate(
							`${row.cancellationId}/reinstatements/${reinstatement.data.locator}/details`,
						);
					}
				} catch (error) {
					errorData.handleErrorResponse(
						error,
						'Failed to reinstate cancellation',
					);
					await handleUnauthorizedResponse(error, authData);
				}

				setLoading(false);
			};

			return (
				<TableRow
					hover
					component={Link}
					to={row.link}
					tabIndex={-1}
					key={row.cancellationId}
					className={classes.tableRow}
				>
					<TableCell id={row.cancellationId} scope='row' padding='none'>
						{row.cancellationId}
					</TableCell>
					<TableCell>{authData.getFormattedDate(row.dateCreated)}</TableCell>
					<TableCell>{authData.getFormattedDate(row.effectiveDate)}</TableCell>
					<TableCell>{row.status}</TableCell>
					{row.showReinstate ? (
						<TableCell onClick={(e) => e.preventDefault()}>
							<TableActionConfirmationTrigger
								label='Reinstate'
								message='Are you sure you want to reinstate this cancellation?'
								confirmText='Reinstate Cancellation'
								onConfirm={reinstate}
							/>
						</TableCell>
					) : (
						<TableCell />
					)}
				</TableRow>
			);
		},
	);

	return (
		<>
			{loading ? <LargeCenterSpinner /> : null}
			<SortableTable
				rows={rowElements}
				headCells={headCells}
				additionalMsg={emptyTable}
				order={order}
				setOrder={setOrder}
				orderBy={orderBy}
				setOrderBy={setOrderBy}
			/>
		</>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'authData' | 'errorData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ErrorContext.Consumer>
					{(errorData) => (
						<Component {...props} authData={authData} errorData={errorData} />
					)}
				</ErrorContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(CancellationsTable);
